import Immutable from 'immutable';
import mapValues from 'lodash/mapValues';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';

import {
  trackBibClick as trackBibClickAnalytics,
  trackBibsRowTitleClick as trackBibsRowTitleClickAnalytics,
  trackLinksRowLinkClick as trackLinksRowLinkClickAnalytics,
  trackListCreatorClick as trackListCreatorClickAnalytics,
  trackListImageClick as trackListImageClickAnalytics,
  trackListImpression as trackListImpressionAnalytics,
  trackListTitleClick as trackListTitleClickAnalytics,
  trackNavLinkClick as trackNavLinkClickAnalytics
} from 'app/actions/AnalyticsGa4Actions/browse/rowsActions';
import BrowseHomePage from 'app/components/shared/browse/BrowseHomePage';
import ProgressBar from 'app/components/shared/ProgressBar';
import { getRelationships } from 'app/actions/RelationshipsActions';
import { BackToLinkManager } from 'app/components/BackToLinkManager';
import ErrorPage from 'app/components/ErrorPage';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { mapSearchBibAvailabilityToDisplayAvailability } from 'app/helpers/displayBib';
import { mapBrowseConfigElementsToRows } from 'app/helpers/browse/rows';

import useFeature from 'app/hooks/useFeature';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectBrowseHomeConfig, selectIsFetching } from 'app/selectors/browse/browseHomeSelector';
import { selectBibEntities, selectListEntities, selectUserEntities } from 'app/selectors/EntitiesSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import BrowseHomePageLoadingStencil from './BrowseHomePageLoadingStencil';

const BrowseHomePageContainer = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const currentLibrary = useSelector(selectCurrentLibrary);
  const currentUser = useSelector(selectCurrentUser);
  const config = useSelector(selectBrowseHomeConfig) ?? Immutable.Map();
  const searchBibByMetadataId = useSelector(selectBibEntities);
  const isFetching = useSelector(selectIsFetching);
  const listsById = useSelector(selectListEntities);
  const usersById = useSelector(selectUserEntities);
  const isCoreV2BrowsePageEnabled =
    useFeature(TOGGLZ_FEATURES.CORE_V2_BROWSE_PAGE) && (currentLibrary?.getIn(['browse', 'homePageEnabled']) ?? false);

  const pageTitle = intl.formatMessage({ defaultMessage: 'Browse', id: 'browse' });

  useEffect(() => {
    if (currentUser?.isEmpty() ?? true) {
      return;
    }

    const listCreatorIds = listsById.valueSeq().map(list => list.get('owner'));

    dispatch(getRelationships({ userId: currentUser.get('id'), userIds: listCreatorIds }));
  }, [currentUser, dispatch, listsById]);

  const [isShowStencil, setIsShowStencil] = useState(false);
  useEffect(() => {
    let timer;
    if (isFetching) {
      timer = setTimeout(() => setIsShowStencil(true), 200);
    } else {
      setIsShowStencil(false);
    }
    return () => clearTimeout(timer);
  }, [isFetching]);

  if (!isCoreV2BrowsePageEnabled) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const displayAvailabilityByMetadataId = mapValues(searchBibByMetadataId?.toJS() ?? {}, searchBib => {
    const { availability } = searchBib;
    return mapSearchBibAvailabilityToDisplayAvailability(availability);
  });

  const rows = mapBrowseConfigElementsToRows(config.get('elements', Immutable.List())?.toJS(), {
    hasHistoryRoutingForBibTitles: false,
    hasHistoryRoutingForNavLinks: false,
    listById: listsById?.toJS(),
    searchBibByMetadataId: searchBibByMetadataId?.toJS(),
    userById: usersById?.toJS()
  });

  const trackBibClick = payload => {
    const { displayBib } = payload;
    const { metadataId } = displayBib;
    const displayAvailability = displayAvailabilityByMetadataId[metadataId] || {};

    dispatch(
      trackBibClickAnalytics({
        displayAvailability,
        ...payload
      })
    );
  };

  const trackBibsRowTitleClick = payload => dispatch(trackBibsRowTitleClickAnalytics(payload));
  const trackLinksRowLinkClick = payload => dispatch(trackLinksRowLinkClickAnalytics(payload));
  const trackListCreatorClick = payload => dispatch(trackListCreatorClickAnalytics(payload));
  const trackListImageClick = payload => dispatch(trackListImageClickAnalytics(payload));
  const trackListImpression = payload => dispatch(trackListImpressionAnalytics(payload));
  const trackListTitleClick = payload => dispatch(trackListTitleClickAnalytics(payload));
  const trackNavLinkClick = payload => dispatch(trackNavLinkClickAnalytics(payload));

  return (
    <div style={{ minHeight: '480px' }}>
      <DocumentHeadContainer
        description={intl.formatMessage(
          { id: 'browse_and_discover_description' },
          { libraryName: currentLibrary?.get('fullName') }
        )}
        pageTitle={pageTitle}
      />
      <ProgressBar active={isFetching} />
      <BackToLinkManager pageId="browse" />
      <CSSTransition classNames="fade" exit={false} in={isShowStencil} timeout={500} unmountOnExit>
        <BrowseHomePageLoadingStencil />
      </CSSTransition>
      {!isFetching && (
        <BrowseHomePage
          pageTitle={pageTitle}
          rows={rows}
          trackBibClick={trackBibClick}
          trackBibsRowTitleClick={trackBibsRowTitleClick}
          trackLinksRowLinkClick={trackLinksRowLinkClick}
          trackListCreatorClick={trackListCreatorClick}
          trackListImageClick={trackListImageClick}
          trackListImpression={trackListImpression}
          trackListTitleClick={trackListTitleClick}
          trackNavLinkClick={trackNavLinkClick}
        />
      )}
    </div>
  );
};

export default BrowseHomePageContainer;
